import { useMemo, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { BILL } from "../constants/testIds2";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { DownloadSimple, ChartLine } from "@phosphor-icons/react";
import { Badge } from "../components/ui/badge";

function toCsv(rows) {
  if (!rows.length) return "";
  const keys = Object.keys(rows[0]);
  return [keys.join(","), ...rows.map((r) => keys.map((k) => JSON.stringify(r[k] ?? "")).join(","))].join("\n");
}

export default function InvoiceData() {
  const { user, tenantId } = useAuth();
  const today = new Date();
  const [dateFrom, setDateFrom] = useState(new Date(today.getFullYear(), today.getMonth(), 1).toISOString().slice(0, 10));
  const [dateTo, setDateTo] = useState(today.toISOString().slice(0, 10));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const run = () => {
    setLoading(true);
    const params = { date_from: dateFrom, date_to: dateTo };
    if (user?.role === "super_admin" && tenantId) params.tenant_id = tenantId;
    api.get("/billing/summary", { params }).then(({ data }) => setData(data)).finally(() => setLoading(false));
  };

  const grouped = useMemo(() => {
    if (!data?.rows) return {};
    const g = {};
    for (const r of data.rows) {
      const key = r.client_id + "|" + r.client_name;
      if (!g[key]) g[key] = { client_name: r.client_name, client_code: r.client_code, currency: r.currency, hours: 0, amount: 0, projects: {} };
      g[key].hours = Math.round((g[key].hours + r.hours) * 100) / 100;
      g[key].amount = Math.round((g[key].amount + r.amount) * 100) / 100;
      const pk = r.project_id + "|" + r.project_name;
      if (!g[key].projects[pk]) g[key].projects[pk] = { name: r.project_name, code: r.project_code, hours: 0, amount: 0, rows: [] };
      g[key].projects[pk].hours = Math.round((g[key].projects[pk].hours + r.hours) * 100) / 100;
      g[key].projects[pk].amount = Math.round((g[key].projects[pk].amount + r.amount) * 100) / 100;
      g[key].projects[pk].rows.push(r);
    }
    return g;
  }, [data]);

  const download = () => {
    if (!data?.rows?.length) return;
    const blob = new Blob([toCsv(data.rows)], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `invoice-data-${dateFrom}-${dateTo}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-emerald-700">Billing</div>
          <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight">Invoice data export</h1>
          <p className="text-sm text-zinc-500 mt-2">Approved billable hours × contract rate — ready for finance and invoicing.</p>
        </div>
        <div className="flex items-end gap-3">
          <div><Label className="text-xs">From</Label><Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="rounded-sm" /></div>
          <div><Label className="text-xs">To</Label><Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="rounded-sm" /></div>
          <Button onClick={run} className="btn-zenith rounded-sm" data-testid={BILL.runBtn}><ChartLine size={16} className="mr-1.5" /> Compute</Button>
          <Button onClick={download} disabled={!data?.rows?.length} variant="outline" className="rounded-sm" data-testid={BILL.exportBtn}><DownloadSimple size={16} className="mr-1.5" /> CSV</Button>
        </div>
      </div>

      {loading && <div className="text-sm text-zinc-500">Computing…</div>}

      {data && (
        <div className="space-y-4" data-testid={BILL.summary}>
          {/* Totals by currency */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {Object.entries(data.totals_by_currency).map(([cur, t]) => (
              <div key={cur} className="border border-zinc-200 bg-white rounded-sm p-4">
                <div className="text-xs uppercase tracking-widest text-zinc-500">{cur} total</div>
                <div className="font-display text-3xl font-bold mt-1">{t.amount.toLocaleString("en-IN")}</div>
                <div className="text-xs text-zinc-500 mt-1">{t.hours}h billable</div>
              </div>
            ))}
            {Object.keys(data.totals_by_currency).length === 0 && (
              <div className="col-span-full border border-zinc-200 bg-white p-8 text-center text-sm text-zinc-500 rounded-sm">
                No approved billable timesheets for this range.
              </div>
            )}
          </div>

          {/* Client breakdown */}
          {Object.values(grouped).map((c, i) => (
            <div key={i} className="border border-zinc-200 rounded-sm bg-white">
              <div className="p-4 border-b border-zinc-200 flex items-center justify-between">
                <div>
                  <div className="font-display text-lg font-bold">{c.client_name} <span className="font-mono-plex text-xs text-zinc-500">· {c.client_code}</span></div>
                  <div className="text-xs text-zinc-500 mt-0.5">{c.hours}h billable</div>
                </div>
                <div className="text-right">
                  <div className="font-mono-plex text-2xl font-bold">{c.currency} {c.amount.toLocaleString("en-IN")}</div>
                </div>
              </div>
              <div className="p-4 space-y-2">
                {Object.values(c.projects).map((p, j) => (
                  <div key={j} className="border-l-2 border-emerald-200 pl-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-sm font-semibold">{p.name}</div>
                        <div className="text-xs font-mono-plex text-zinc-500">{p.code} · {p.hours}h</div>
                      </div>
                      <div className="font-mono-plex text-sm font-semibold">{c.currency} {p.amount.toLocaleString("en-IN")}</div>
                    </div>
                    <div className="mt-1 grid grid-cols-1 md:grid-cols-2 gap-1">
                      {p.rows.map((r, k) => (
                        <div key={k} className="text-xs text-zinc-600 flex justify-between">
                          <span>{r.employee_name} <span className="font-mono-plex text-zinc-400">· {r.employee_code}</span></span>
                          <span className="font-mono-plex">{r.hours}h @ {r.currency} {r.rate_amount}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
